import styled, { css, keyframes } from 'styled-components';

/**
 * WSPOLNE PRYMITYWY EKRANU.
 *
 * Ten plik istnial juz wczesniej, ale korzystalo z niego 18 z okolo 40 stron. Reszta miala
 * WLASNE kopie tych samych rzeczy — audyt z 2026-08-19 naliczyl 164 definicje `styled.button`,
 * w tym 13 osobnych `PrimaryButton` i 11 `SecondaryButton`. Kopie zdazyly sie rozjechac:
 * przycisk glowny na Szczegolach sprzetu byl FIOLETOWY (#7c3aed), w Kalendarzu sprzetu mial
 * promien 10px i wage 700, w Fakturach padding 12/22 i krok pisma 14px. Nikt tego nie
 * zaplanowal — to przezyte kopie.
 *
 * Zasada: strona NIE definiuje wlasnego przycisku, naglowka, karty ani odznaki. Jesli czegos
 * tu brakuje, dokladamy TUTAJ, zamiast robic czternasty wariant u siebie.
 *
 * Wszystkie wartosci pochodza z tokenow w `src/index.css` (--sp-*, --r-*, --fs-*, --dur, --ease,
 * kolory znaczeniowe). Zadnych liczb ani kolorow wpisanych wprost.
 */

// Animacje
// Wejście strony przesuwa kontener przez `top`, NIE przez `transform`. Element z transformacją
// (także w trakcie animacji) staje się blokiem zawierającym dla potomków `position: fixed`, więc
// przez 0,6 s okno otwarte zaraz po wejściu (np. szczegóły z `/calendar?reservationId=`) było
// liczone względem panelu strony zamiast okna przeglądarki: węższe, przycięte `overflow: hidden`
// panelu i przeskakujące po końcu animacji (niestabilny reservation_edit_narrow_dates.cy.js).
// `top` działa na kontenerach z `position: relative` (wszystkie użycia tej animacji je mają)
// i bloku zawierającego nie tworzy. Pilnuje tego ui/__tests__/slideIn.test.js.
export const slideIn = keyframes`
  from { opacity: 0; top: 20px; }
  to { opacity: 1; top: 0; }
`;

/**
 * Wlos w kolorze ramki na gornej krawedzi panelu strony.
 *
 * Dawniej kazda strona miala wlasna kopie: pasek 3px na `::before` z `border-radius: 16px
 * 16px 0 0`. Promien 16px na elemencie 3px przeglarka zmniejsza do ~3px, wiec pasek byl
 * prawie prostokatem na CALA szerokosc — ladnie wygladal tylko tam, gdzie kontener mial
 * `overflow: hidden` i przycinal go do zaokraglonych rogow. Strony z `overflow: visible`
 * (dropdowny, popper kalendarza) pokazywaly pasek wystajacy poza rogi panelu.
 *
 * Tutaj pseudo-element pokrywa CALY panel i dziedziczy jego promien, a pasek jest tlem
 * wysokim na 3px — tlo zawsze przycina sie do zaokraglenia wlasnego elementu, wiec wynik
 * jest identyczny niezaleznie od `overflow` kontenera i od promienia z media queries.
 * `pointer-events: none`, bo nakladka lezy nad trescia. Kontener musi miec `position: relative`.
 */
export const panelTopHairline = css`
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(var(--border), var(--border)) top / 100% 3px no-repeat;
    pointer-events: none;
  }
`;

// Główny kontener panelu/strony
export const PanelContainer = styled.div`
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(var(--sp-4), 2.5vw, var(--sp-8));
  background: var(--panel-bg);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-md);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  position: relative;
  overflow: hidden;
  animation: ${slideIn} 0.6s ease-out;
  min-height: calc(100vh - 48px);
  box-sizing: border-box;

  @media (min-width: 1400px) {
    max-width: 1600px;
    padding: var(--sp-8) 40px;
  }

  @media (max-width: 768px) {
    padding: var(--sp-4);
    border-radius: var(--r-md);
  }

  ${panelTopHairline}
`;

// ── Nagłówki ────────────────────────────────────────────────────────────────
/**
 * Jeden rozmiar tytulu strony zamiast pieciu. Przed ujednoliceniem w aplikacji byly:
 * 2rem (12 stron), 2.25rem (3 strony), 1.75rem, 1.6rem i dwa rozne `clamp()`. Do tego
 * dwie strony uzywaly jako tytulu `<h2>`, wiec ich drzewo naglowkow zaczynalo sie od
 * drugiego poziomu.
 */
export const Title = styled.h1`
  margin: 0 0 var(--sp-2) 0;
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.025em;

  @media (max-width: 768px) {
    font-size: var(--fs-xl);
  }
`;

export const Subtitle = styled.p`
  margin: 0;
  font-size: clamp(var(--fs-sm), 1.5vw, 1.05rem);
  color: var(--text-muted);
`;

/** Tytul sekcji WEWNATRZ strony (drugi poziom). */
export const SectionTitle = styled.h2`
  margin: 0 0 var(--sp-3) 0;
  font-size: var(--fs-lg);
  font-weight: 700;
  color: var(--text);
`;

/**
 * Pasek naglowka strony: blok tytulu po lewej, akcje po prawej, na telefonie jedno pod drugim.
 * Kazda strona skladala to dotad sama (`PageHeader`, `HeaderRow`, `HeaderBlock`, `Header`,
 * `TitleSection` — piec nazw na ten sam uklad).
 */
export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--sp-4);
  margin-bottom: var(--sp-6);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

/**
 * Wiersz "strzałka wstecz + tytuł" na podstronach profilu (Moje dane, Bezpieczeństwo,
 * Ustawienia…) — na wejściu w PageHeader zamiast gołego Title. Powstał z tego samego powodu co
 * PageHeader wyżej: każda podstrona składała sobie własny przycisk powrotu (inny rozmiar, inne
 * obramowanie, raz obok tytułu, raz nad nim), więc "wróć do profilu" wyglądało za każdym razem
 * inaczej. Użyj z IconButton + ArrowLeftIcon z ui/icons.js.
 */
export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
`;

// ── Karty ───────────────────────────────────────────────────────────────────
export const PanelCard = styled.div`
  background: var(--card-bg);
  border-radius: var(--r-lg);
  border: 1px solid var(--input-border);
  box-shadow: var(--shadow-sm);
  padding: var(--sp-6);
  margin-bottom: var(--sp-6);
`;

/**
 * Kafelek liczbowy (KPI). Byly CZTERY implementacje tego samego kafelka: start, klienci,
 * magazyn i platnosci — z promieniami 12/12/12/14, ramkami 1/1/2/1 px, paddingiem 20/20/14/16
 * i trzema roznymi cieniami pod kursorem. Stad jedna definicja i skala uniesienia zamiast
 * trzech wlasnych cieni.
 */
export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--sp-4);
  margin-bottom: var(--sp-6);
`;

export const StatCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  box-shadow: var(--shadow-sm);
  padding: var(--sp-5);
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  transition: box-shadow var(--dur) var(--ease), transform var(--dur) var(--ease);

  /* Uniesienie pod kursorem TYLKO dla kafelka, ktory faktycznie cos robi po kliknieciu.
     Wczesniej kafelki nieklikalne tez sie unosily, wiec sygnal „to jest przycisk" nic nie znaczyl. */
  ${(p) => p.$clickable && `
    cursor: pointer;
    &:hover { box-shadow: var(--shadow-md); transform: translateY(-2px); }
  `}
`;

/**
 * PALETA AKCENTÓW DEKORACYJNYCH IKON (nie mylić z kolorami znaczeniowymi wyżej).
 *
 * Aplikacja miała jeden nawyk powtórzony w kilkunastu plikach: kwadratowa plakietka z ikoną,
 * wypełniona na pełno `var(--primary)`, z białą ikoną na wierzchu. Osobno w każdym pliku
 * (`PanelIcon` w GenerateOffer/reservation/Security/Preferences, `FormPanelIcon` w kalendarzu,
 * `TitleIcon` w nagłówkach stron, `KPIStatIcon` na home, `ClientAvatar` na liście klientów) —
 * 11+ niezależnych kopii tego samego pomysłu, zawsze w tym samym niebieskim. To dokładnie wzorzec
 * "plakietka-ikony" rozpoznawalny z każdego wygenerowanego przez AI dashboardu, a powtórzony
 * wszędzie tym samym kolorem czyni z niego szum, nie sygnał.
 *
 * Te pięć tonów (nazwane po tym, co przywołują, nie po miejscu użycia) to jedyne źródło prawdy
 * dla tego wzorca w całej aplikacji — każdy plik z plakietką importuje stąd, żaden nie definiuje
 * własnych heksów. Miękki podkolorowany kafelek (tło+obrys+ikona w tym samym tonie), ten sam
 * trójkowy wzorzec co tokeny znaczeniowe (`--success/-bg/-border`), zamiast pełnego wypełnienia
 * z białą ikoną. Świadomie OSOBNE od `--success/--danger/--warning/--info`: te ikony nie niosą
 * stanu (błąd/ostrzeżenie), więc pomieszanie palet sprawiłoby, że np. zielona plakietka sprzętu
 * czytałaby się jak komunikat sukcesu. Znaczenie tonu nadaje wywołujący (patrz przykłady niżej),
 * nie sam token — ale w całej aplikacji trzymana jest jedna spójna asocjacja:
 *   steel (stal/lód)   — tożsamość, konto, klient, osoba
 *   clay  (glina/drewno) — dokumenty, notatki, zapisy, firma
 *   pine  (świerk)     — sprzęt, magazyn, dostępność
 *   dusk  (zmierzch)   — czas, terminy, kalendarz, urządzenia/technika
 *   gold  (złoto)      — pieniądze, płatność, podsumowanie, wskazówki
 */
export const ICON_TONES = {
  steel: {
    fg: '#3d5a76', bg: '#eef2f7', border: '#d7e0ea',
    fgDark: '#9fb8d1', bgDark: 'rgba(96,140,181,0.16)', borderDark: 'rgba(96,140,181,0.34)',
  },
  clay: {
    fg: '#a35a2f', bg: '#fdf1e9', border: '#f3d9c4',
    fgDark: '#e3a679', bgDark: 'rgba(211,124,68,0.16)', borderDark: 'rgba(211,124,68,0.34)',
  },
  pine: {
    fg: '#2f7d68', bg: '#eaf6f2', border: '#cdebe1',
    fgDark: '#7fd4bb', bgDark: 'rgba(63,166,140,0.16)', borderDark: 'rgba(63,166,140,0.34)',
  },
  dusk: {
    fg: '#6b4f96', bg: '#f3eefa', border: '#e0d3f0',
    fgDark: '#c3aae8', bgDark: 'rgba(139,105,196,0.16)', borderDark: 'rgba(139,105,196,0.34)',
  },
  gold: {
    fg: '#96660f', bg: '#fdf4e3', border: '#f2ddab',
    fgDark: '#e8c477', bgDark: 'rgba(214,158,46,0.16)', borderDark: 'rgba(214,158,46,0.34)',
  },
};

const SEMANTIC_TONES = ['danger', 'success', 'warning', 'info'];

// Wspólna logika kolorowania plakietki — używana przez StatIcon (kafelek KPI) i IconBadge
// (nagłówek panelu/sekcji, ikona strony, awatar). `$tone` przyjmuje albo klucz z ICON_TONES
// (dekoracyjny), albo jeden z tokenów znaczeniowych ('danger' itd. — np. konflikt rezerwacji
// naprawdę POWINIEN zostać czerwony, nie dostawać przypadkowego tonu z palety dekoracyjnej).
const badgeTone = (p) => {
  if (p.$muted) {
    return `background: var(--panel-bg); color: var(--text-muted); border-color: var(--input-border);`;
  }
  if (p.$tone && SEMANTIC_TONES.includes(p.$tone)) {
    return `
      background: var(--${p.$tone}-bg);
      color: var(--${p.$tone});
      border-color: var(--${p.$tone}-border);
    `;
  }
  const tone = ICON_TONES[p.$tone] || ICON_TONES.steel;
  return `
    background: ${tone.bg};
    color: ${tone.fg};
    border-color: ${tone.border};

    [data-theme='dark'] & {
      background: ${tone.bgDark};
      color: ${tone.fgDark};
      border-color: ${tone.borderDark};
    }
  `;
};

export const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => p.$size || 40}px;
  height: ${(p) => p.$size || 40}px;
  flex-shrink: 0;
  border-radius: var(--r-md);
  border: 1.5px solid;
  ${badgeTone}
`;

// Alias — ten sam komponent, nazwa czytelna obok tytułu strony/panelu, a nie tylko przy
// kafelku statystyki. Zero powielonego stylu: to StatIcon pod drugą nazwą.
export const IconBadge = StatIcon;

export const StatValue = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--text);
  line-height: 1.2;
`;

export const StatLabel = styled.div`
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;

// ── Odznaki ─────────────────────────────────────────────────────────────────
/**
 * Odznaka stanu. `tone` to nazwa koloru znaczeniowego z index.css, wiec odznaka dziala
 * w obu motywach bez zadnych wyjatkow po stronie strony. Wczesniej kazda lista niosla
 * wlasna mape statusow na szesciocyfrowe kolory (`Invoices.js` i `InvoicePreview.js` mialy
 * DWIE KOPIE tej samej mapy, w dodatku z roznymi etykietami tego samego stanu).
 */
export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);
  padding: var(--sp-1) var(--sp-2);
  border-radius: var(--r-pill);
  font-size: var(--fs-xs);
  font-weight: 700;
  white-space: nowrap;
  color: var(--${(p) => p.$tone || 'info'});
  background: var(--${(p) => p.$tone || 'info'}-bg);
  border: 1px solid var(--${(p) => p.$tone || 'info'}-border);
`;

// ── Formularze ──────────────────────────────────────────────────────────────
export const FilterSelect = styled.select`
  width: 100%;
  padding: var(--sp-3) var(--sp-4);
  border: 2px solid var(--input-border);
  border-radius: var(--r-md);
  font-size: var(--fs-md);
  font-weight: 500;
  background: var(--input-bg);
  transition: all var(--dur) var(--ease);
  box-sizing: border-box;
  color: var(--text);

  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }
`;

export const TextInput = styled.input`
  width: 100%;
  padding: var(--sp-3) var(--sp-4);
  border: 2px solid var(--input-border);
  border-radius: var(--r-md);
  font-size: var(--fs-md);
  background: var(--input-bg);
  color: var(--text);
  box-sizing: border-box;
  transition: border-color var(--dur) var(--ease), box-shadow var(--dur) var(--ease);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
`;

/** Pole wieloliniowe — ten sam wyglad co TextInput. Faktury mialy dwie lokalne kopie (edytor, korekta). */
export const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: var(--sp-3) var(--sp-4);
  border: 2px solid var(--input-border);
  border-radius: var(--r-md);
  font-family: inherit;
  font-size: var(--fs-md);
  background: var(--input-bg);
  color: var(--text);
  box-sizing: border-box;
  resize: vertical;
  transition: border-color var(--dur) var(--ease), box-shadow var(--dur) var(--ease);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
`;

/**
 * Etykieta pola. `htmlFor` jest tu ZWYKLYM propem, ktory trzeba podac — i o to chodzi:
 * w aplikacji tylko 6 z 49 etykiet mialo powiazanie z polem, wiec klikniecie w etykiete
 * nie ustawialo fokusu, a czytnik ekranu jej nie zapowiadal.
 */
export const FieldLabel = styled.label`
  display: block;
  margin-bottom: var(--sp-2);
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--text);
`;

/** Gwiazdka pola wymaganego — dotad dopisywana recznie na 10 stronach. */
export const RequiredMark = styled.span.attrs({ 'aria-hidden': 'true' })`
  color: var(--danger);
  margin-left: 2px;
`;

/** Podpowiedz pod polem (nie blad — blad ma `Banner` w wariancie `compact`). */
export const FieldHint = styled.div`
  margin-top: var(--sp-1);
  font-size: var(--fs-xs);
  color: var(--text-muted);
`;

// ── Przyciski ───────────────────────────────────────────────────────────────
export const PrimaryButton = styled.button`
  background: var(--primary);
  color: var(--primary-contrast);
  border: none;
  padding: clamp(10px, 1.5vw, var(--sp-3)) clamp(18px, 2vw, var(--sp-6));
  border-radius: var(--r-md);
  font-size: clamp(var(--fs-sm), 1.2vw, var(--fs-md));
  font-weight: 600;
  cursor: pointer;
  transition: all var(--dur) var(--ease);
  box-shadow: var(--shadow-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  white-space: nowrap;

  @media (max-width: 768px) {
    width: 100%;
    padding: var(--sp-3) var(--sp-4);
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: var(--primary-hover);
  }

  &:disabled { opacity: .6; cursor: not-allowed; }
`;

export const SecondaryButton = styled.button`
  background: var(--card-bg);
  color: var(--text);
  border: 1px solid var(--input-border);
  padding: clamp(10px, 1.5vw, var(--sp-3)) clamp(var(--sp-4), 2vw, var(--sp-5));
  border-radius: var(--r-md);
  font-size: clamp(var(--fs-sm), 1.2vw, var(--fs-base));
  font-weight: 600;
  cursor: pointer;
  transition: all var(--dur) var(--ease);
  min-width: 100px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  white-space: nowrap;

  @media (max-width: 768px) {
    width: 100%;
    padding: var(--sp-3) var(--sp-4);
  }

  &:hover:not(:disabled) {
    background: var(--hover-bg);
    transform: translateY(-1px);
  }

  &:disabled { opacity: .6; cursor: not-allowed; }
`;

/** Akcja nieodwracalna (usuniecie). Wczesniej robiona przez `background: #dc2626` na stronie. */
export const DangerButton = styled(PrimaryButton)`
  background: var(--danger);
  box-shadow: none;

  &:hover:not(:disabled) {
    background: var(--danger);
    filter: brightness(0.92);
  }
`;

/**
 * Przycisk bez tla — do akcji w wierszu tabeli. `aria-label` jest OBOWIAZKOWY, gdy w srodku
 * jest sama ikona: w aplikacji bylo 43 `aria-label` na 113 uzyc `title=`, a `title` nie jest
 * zamiennikiem (nie dziala dotykiem i bywa pomijany przez czytniki).
 */
export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-1);
  padding: var(--sp-2);
  min-width: 36px;
  min-height: 36px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--r-sm);
  color: ${(p) => p.$tone ? `var(--${p.$tone})` : 'var(--text-muted)'};
  cursor: pointer;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);

  &:hover:not(:disabled) {
    background: var(--hover-bg);
    color: ${(p) => p.$tone ? `var(--${p.$tone})` : 'var(--text)'};
  }

  &:disabled { opacity: .5; cursor: not-allowed; }
`;

/**
 * Akcja tekstowa: odnosnik w wierszu tabeli, pod polem, obok licznika wynikow. Byly trzy lokalne
 * kopie (`SourceLink` w Platnosciach, `ChangeClientButton` w edytorze faktur, przyciski KSeF na
 * liscie faktur robione pelnym SecondaryButton, przez co kazdy wiersz mial dwa duze przyciski).
 * `$tone` to kolor znaczeniowy (`danger` dla wycofania), `$tone="inherit"` bierze kolor otoczenia.
 */
export const LinkButton = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  padding: 0;
  color: ${(p) => (p.$tone === 'inherit' ? 'inherit' : p.$tone ? `var(--${p.$tone})` : 'var(--primary)')};
  font: inherit;
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) { text-decoration: underline; }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 2px; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

// ── Filtry list ─────────────────────────────────────────────────────────────
/**
 * Etykieta filtra nad kontrolka (male wersaliki). Faktury i Platnosci mialy dwie kopie
 * (`FilterField` i `Field`), rozniace sie tylko rozstrzeleniem liter.
 */
export const FilterField = styled.label`
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
  min-width: 0;
  font-size: var(--fs-xs);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
`;

export const FilterPillGroup = styled.div`
  display: flex;
  gap: var(--sp-1);
  flex-wrap: wrap;
`;

/**
 * Pigulka filtra stanu. Aktywna przyjmuje kolory tego samego tonu (`$tone`), co odznaka `Badge`
 * w tabeli pod spodem, wiec „Nieoplacone" w filtrze i „Nieoplacona" w wierszu czytaja sie jak
 * jedna rzecz. Wydzielona z Platnosci, gdy ten sam filtr dostaly Faktury. `aria-pressed` mowi
 * czytnikowi ekranu, ktora pigulka jest wlaczona — sam kolor tego nie przekazuje.
 */
export const FilterPill = styled.button.attrs((p) => ({ type: 'button', 'aria-pressed': !!p.$active }))`
  border-radius: var(--r-pill);
  padding: var(--sp-2) var(--sp-4);
  font-family: inherit;
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;
  transition: background var(--dur) var(--ease), border-color var(--dur) var(--ease), color var(--dur) var(--ease);
  border: 1px solid ${(p) => (p.$active ? 'transparent' : 'var(--input-border)')};
  background: ${(p) => (p.$active ? (p.$tone ? `var(--${p.$tone}-bg)` : 'var(--hover-bg)') : 'transparent')};
  color: ${(p) => (p.$active ? (p.$tone ? `var(--${p.$tone})` : 'var(--text)') : 'var(--text-muted)')};

  &:hover:not(:disabled) {
    border-color: ${(p) => (p.$tone ? `var(--${p.$tone})` : 'var(--primary)')};
    color: ${(p) => (p.$tone ? `var(--${p.$tone})` : 'var(--text)')};
  }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
  /* Wylaczona pigulka nie moze wygladac na klikalna: bez tego miala kursor reki i podswietlala
     sie po najechaniu, choc klikniecie nic nie robilo. */
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ── Tabele ──────────────────────────────────────────────────────────────────
/**
 * Pojemnik tabeli. Tabela ma przewijac sie POZIOMO WEWNATRZ tego pojemnika — bez tego
 * szeroka tabela rozpycha cala strone i na telefonie pojawia sie poziomy pasek dokumentu.
 * W aplikacji bylo 13 plikow z tabelami i tylko 6 zapisow `overflow-x: auto`.
 */
export const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: var(--r-md);
  border: 1px solid var(--border);
  background: var(--card-bg);
  -webkit-overflow-scrolling: touch;
`;

// ── Paski narzedzi konstruktorow ────────────────────────────────────────────
/**
 * Przycisk paska narzedzi — nizszy i cichszy niz PrimaryButton/SecondaryButton, do rzedow
 * akcji nad tresc (zapis, publikacja, podglad PDF, przelaczniki widoku). Powstal dla dwoch
 * konstruktorow (umowy i oferty), zeby ich paski akcji byly identyczne; wczesniej kazdy z nich
 * mial wlasny przycisk, a w konstruktorze umowy domyslnym tlem bylo `var(--text-muted)`, wiec
 * „Cofnij" wygladalo tak samo waznie jak „Publikuj szablon".
 *
 * Wariant `$primary` (pelne tlo akcentem) ma dostac DOKLADNIE JEDEN przycisk w pasku — reszta
 * zostaje cicha. Kolor stanu (np. zielen publikacji) dokladaj przez `styled(ToolbarButton)`
 * u siebie, a nie kolejnym propem tutaj.
 */
export const ToolbarButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-4);
  border-radius: var(--r-md);
  border: 1px solid ${(p) => (p.$primary ? 'transparent' : 'var(--input-border)')};
  background: ${(p) => (p.$primary ? 'var(--primary)' : 'var(--card-bg)')};
  color: ${(p) => (p.$primary ? 'var(--primary-contrast)' : 'var(--text)')};
  font-family: inherit;
  font-size: var(--fs-sm);
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  /* Animujemy tylko wyglad, NIE szerokosc — inaczej zmiana etykiety („Generowanie...")
     animuje szerokosc przycisku i caly pasek „duchuje". */
  transition: background var(--dur) var(--ease), border-color var(--dur) var(--ease), color var(--dur) var(--ease);

  &:hover:not(:disabled) {
    background: ${(p) => (p.$primary ? 'var(--primary-hover)' : 'var(--hover-bg)')};
    border-color: ${(p) => (p.$primary ? 'transparent' : 'var(--border)')};
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/**
 * Przelacznik wlacz/wylacz. Stan podaje `$on`, a uzycie dodaje `role="switch"`, `aria-checked`
 * i podpis: sam kolor niczego nie mowi czytnikowi ekranu.
 */
export const ToggleSwitch = styled.button`
  position: relative;
  width: 46px;
  height: 26px;
  border-radius: 13px;
  border: 1.5px solid ${(p) => (p.$on ? 'var(--primary)' : 'var(--input-border)')};
  background: ${(p) => (p.$on ? 'var(--primary)' : 'var(--input-bg)')};
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$on ? '21px' : '2px')};
    width: 19px;
    height: 19px;
    border-radius: 50%;
    background: ${(p) => (p.$on ? 'white' : 'var(--text-muted)')};
    transition: left 0.15s ease;
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

/**
 * Pigulka „wstaw pole" — klikniecie wstawia zmienna ({{firstName}}, {{nrOferty}}...) do
 * edytowanego tekstu. Przerywana ramka mowi „tu wskoczy wartosc z rezerwacji", zamiast
 * udawac zwykla akcje.
 *
 * DLACZEGO CICHA: w konstruktorze umowy te pigulki byly pelnymi prostokatami w kolorze
 * akcentu, a jest ich kilkanascie pod kazda sekcja — lista krzyczala glosniej niz edytor
 * tresci nad nia. W konstruktorze oferty mialy z kolei kolor wpisany wprost
 * (`rgba(59,130,246,...)`), czyli jedyny w tym module kolor nieznajacy trybu ciemnego.
 */
export const VariableChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);
  padding: 5px var(--sp-3);
  border: 1px dashed var(--input-border);
  border-radius: var(--r-pill);
  background: var(--card-bg);
  color: var(--text-muted);
  font-family: inherit;
  font-size: var(--fs-xs);
  font-weight: 600;
  cursor: pointer;
  transition: border-color var(--dur) var(--ease), background var(--dur) var(--ease), color var(--dur) var(--ease);

  &::before {
    content: '+';
    font-weight: 700;
    line-height: 1;
  }

  &:hover:not(:disabled) {
    border-style: solid;
    border-color: var(--primary);
    background: var(--info-bg);
    color: var(--info);
  }

  &:active:not(:disabled) { transform: scale(0.97); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
